/**
 * AvailabilityChip — Badge composition surfacing one of the five J-X22
 * availability states. Used on catalogue cards, the personal dashboard,
 * and the company workspace test list.
 *
 * Inv 15: Badge is consumed from `@/components/ui` barrel. No raw <span> with
 * bespoke chrome.
 */

import { type ReactElement } from 'react';
import { Badge } from '@/components/ui';
import type { BadgeVariant } from '@/components/ui';
import {
  availabilityCopy,
  deriveAvailability,
  type AvailabilityInput,
  type AvailabilityState,
} from '../../lib/availability';

export interface AvailabilityChipProps {
  state?: AvailabilityState;
  /** Alternative to passing state directly — derive it inline. */
  input?: AvailabilityInput;
  /** Override "now" for tests. */
  now?: Date;
  /** When true, return null instead of nothing for always-open (caller hides). */
  hideAlwaysOpen?: boolean;
}

function variantFor(state: AvailabilityState): BadgeVariant {
  switch (state.kind) {
    case 'always_open':
    case 'currently_open':
      return 'success';
    case 'scheduled_future':
      return 'default';
    case 'closed':
      return 'neutral';
    case 'cooldown':
      return 'warning';
  }
}

export function AvailabilityChip(props: AvailabilityChipProps): ReactElement | null {
  const state =
    props.state ??
    (props.input ? deriveAvailability(props.input, props.now) : null);
  if (state === null) return null;
  if (state.kind === 'always_open' && props.hideAlwaysOpen !== false) return null;
  const copy = availabilityCopy(state) ?? 'Available now';
  return <Badge variant={variantFor(state)}>{copy}</Badge>;
}
